// src/pages/tariff/TariffEditPage.js
import React from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getTariff, updateTariff } from '../../api/tariffAPI';
import { getCompany } from '../../api/companyAPI';
import TariffForm from '../../components/tariff/TariffForm';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const TariffEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const tariffId = parseInt(id);

  // Fetch tariff details
  const { data: tariff, isLoading, isError, error } = useQuery({
    queryKey: ['tariff', tariffId],
    queryFn: () => getTariff(tariffId),
    enabled: !!tariffId && !isNaN(tariffId)
  });

  // Fetch company details once we have the tariff
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', tariff?.TariffsCompanyId],
    queryFn: () => getCompany(tariff.TariffsCompanyId),
    enabled: !!tariff?.TariffsCompanyId
  });

  // Update tariff mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateTariff(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tariff', tariffId] });
      queryClient.invalidateQueries({ queryKey: ['tariffs'] });
      if (tariff?.TariffsCompanyId) {
        queryClient.invalidateQueries({ queryKey: ['company-tariffs', tariff.TariffsCompanyId] });
      }
      
      setSnackbar({
        open: true,
        message: 'Tariff updated successfully',
        severity: 'success'
      });
      
      // Navigate back to tariff details page
      setTimeout(() => {
        navigate(`/tariffs/${tariffId}`);
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating tariff: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleSubmit = async (formData) => {
    try {
      await updateMutation.mutateAsync({ id: tariffId, data: formData });
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || !tariff) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading tariff'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          {/* <Breadcrumbs aria-label="breadcrumb" mb={1}>
            <Link component={RouterLink} to="/" color="inherit">
              Dashboard
            </Link>
            <Link component={RouterLink} to="/tariffs" color="inherit">
              Tariffs
            </Link>
            {!isLoadingCompany && company && (
              <Link 
                component={RouterLink} 
                to={`/companies/${tariff.TariffsCompanyId}`} 
                color="inherit"
              >
                {company.CompanyName}
              </Link>
            )}
            <Link 
              component={RouterLink} 
              to={`/tariffs/${tariff.TariffsId}`} 
              color="inherit"
            >
              {tariff.TariffsName}
            </Link>
            <Typography color="textPrimary">Edit</Typography>
          </Breadcrumbs> */}
          <Typography variant="h4">Edit Tariff: {tariff.TariffsName}</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to={`/tariffs/${tariff.TariffsId}`} 
          startIcon={<ArrowBackIcon />}
        >
          Back to Details
        </Button>
      </Box>

      <TariffForm 
        tariff={tariff}
        companyId={tariff.TariffsCompanyId}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
      />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TariffEditPage;